import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LogicType, PermissionService } from 'ngx-eo-web-components';
import { MenuItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../../services/auth/auth.service';
import { BreadcrumbService } from '../../../../services/breadcrumb/breadcrumb.service';
import { CoreService } from '../../core.service';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent implements OnInit, OnDestroy {
  @Output() itemClick = new EventEmitter();
  @Output() showPatientCreateDialog = new EventEmitter();

  previoulsySelectedModule = null;
  currentRoute = '';

  token$ = this.authService.token$;
  roles: string | undefined = '';

  items: MenuItem[] = [];
  subscriptions: Subscription[] = [];
  constructor(
    private readonly router: Router,
    private readonly coreService: CoreService,
    private readonly authService: AuthService,
    private readonly permissionService: PermissionService,
    private readonly breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit(): void {
    // Generate initial menu
    this.items = this.generateMenuItems();

    this.subscriptions.push(
      this.router.events.subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          this.currentRoute = event.url;
          this.items = this.generateMenuItems();
        }
      })
    );

    this.subscriptions.push(
      this.token$.subscribe((t) => {
        this.roles = t?.role;
        this.items = this.generateMenuItems();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }

  hasUserPermission(permissions: string[]): boolean {
    const token = this.authService.token$.getValue();
    if (!token || !permissions || permissions.length === 0) {
      return false;
    }

    // Parse permissions - handle both string (JSON) and array formats
    let userPermissions: string[] = [];
    const permissionsValue = (token as any).permissions;
    if (permissionsValue) {
      if (typeof permissionsValue === 'string') {
        try {
          userPermissions = JSON.parse(permissionsValue);
        } catch (e) {
          console.warn('Failed to parse permissions from token', e);
        }
      } else if (Array.isArray(permissionsValue)) {
        userPermissions = permissionsValue;
      }
    }

    if (userPermissions.length > 0) {
      return permissions.some(permission => userPermissions.includes(permission));
    }

    // Fallback: check functionalities if permissions not available
    let userFunctionalities: string[] = [];
    const functionalitiesValue = (token as any).functionalities;
    if (functionalitiesValue) {
      if (typeof functionalitiesValue === 'string') {
        try {
          userFunctionalities = JSON.parse(functionalitiesValue);
        } catch (e) {
          console.warn('Failed to parse functionalities from token', e);
        }
      } else if (Array.isArray(functionalitiesValue)) {
        userFunctionalities = functionalitiesValue;
      }
    }

    if (userFunctionalities.length > 0) {
      // Map functionalities to permissions for comparison
      const functionalityToPermissionMap: { [key: string]: string[] } = {
        'PRESCRIPTION_VERIFY': ['PRESCRIPTIONS_VIEW', 'OTP_REQUEST', 'OTP_VALIDATE', 'DOCUMENTS_UPLOAD', 'CONTACT', 'SETTINGS_UPDATE'],
        'PHARMACY_MANAGE': ['USER_MANAGEMENT'],
        'ADMIN_VIEW': ['ADMIN_VIEW', 'ROLE_MANAGEMENT', 'users']
      };

      const mappedPermissions: string[] = [];
      userFunctionalities.forEach((func: string) => {
        const mapped = functionalityToPermissionMap[func.toUpperCase()] || [];
        mappedPermissions.push(...mapped);
      });

      return permissions.some(permission => mappedPermissions.includes(permission));
    }

    return false;
  }

  isMenuItemVisible(roles?: string[], permissions?: string[]): boolean {
    // If no roles or permissions are specified, item is visible
    if ((!roles || roles.length === 0) && (!permissions || permissions.length === 0)) {
      return true;
    }

    // Check roles if specified
    if (roles && roles.length > 0) {
      if (this.authService.hasRoles(roles)) {
        return true;
      }
    }

    // Check permissions if specified
    if (permissions && permissions.length > 0) {
      if (this.hasUserPermission(permissions)) {
        return true;
      }
    }

    // If we have roles or permissions specified but none match, hide the item
    return false;
  }

  handleModuleClick(data: any) {
    if (this.previoulsySelectedModule != undefined) {
      (this.previoulsySelectedModule as any).style = {};
      this.previoulsySelectedModule = data;
    }
  }

  menuItem(options: {
    label: string;
    icon: string;
    routerLink?: string;
    url?: string;
    command?: any;
    roles?: string[];
    permissions?: string[];
    permissionLogic?: LogicType;
  }): MenuItem {
    return {
      label: options.label,
      icon: `pi pi-pw ${options.icon}`,
      url: options.url,
      command: () => {
        if (options.routerLink) this.router.navigate([options.routerLink]);
        if (options.command) options.command();
        if (options.routerLink && this.router.url !== options.routerLink)
          this.breadcrumbService.clear();
        this.itemClick.emit();
      },
      visible: this.isMenuItemVisible(options.roles, options.permissions),
      routerLinkActiveOptions: { exact: true },
      styleClass: `${this.currentRoute === options.routerLink ? 'selected' : ''
        }`,
    };
  }

  generateMenuItems(): MenuItem[] {
    return [
      {
        label: 'Λειτουργιες',
        expanded: true,
        items: [
          this.menuItem({
            label: 'Αρχική',
            icon: 'pi pi-pw pi-home',
            routerLink: '/home',
          }),

          // this.menuItem({
          //   label: 'Dummy CRUD',
          //   icon: 'pi pi-pw pi-home',
          //   routerLink: '/dummy-crud',
          //   permissions: ['dummyCrud'],
          // }),

          this.menuItem({
            label: 'Επικύρωση Συνταγών',
            icon: 'pi pi-receipt',
            routerLink: '/prescriptions',
            permissions: ['PRESCRIPTIONS_VIEW'],
          }),

          this.menuItem({
            label: 'Επικυρωμένες Συνταγές',
            icon: 'pi pi-check-circle',
            routerLink: '/prescriptions/verified',
            permissions: ['PRESCRIPTIONS_VIEW'],
          }),

          // this.menuItem({
          //   label: 'Επαλήθευση OTP',
          //   icon: 'pi pi-key',
          //   routerLink: '/otp-verification',
          //   permissions: ['OTP_REQUEST', 'OTP_VALIDATE'],
          // }),

          // this.menuItem({
          //   label: 'Επικύρωση με Έγγραφο',
          //   icon: 'pi pi-upload',
          //   routerLink: '/documents',
          //   permissions: ['DOCUMENTS_UPLOAD'],
          // }),

          // this.menuItem({
          //   label: 'Αρχείο Επικυρώσεων',
          //   icon: 'pi pi-history',
          //   routerLink: '/admin/audit',
          //   roles: ['users'],
          //   permissions: ['ADMIN_VIEW'],
          // }),
        ],
      },
      {
        label: 'Διαχειριση',
        expanded: true,
        visible: this.authService.hasRoles(['Admin', 'Pharmacist', 'ResponsiblePharmacist']),
        items: [
          this.menuItem({
            label: 'Χρήστες Φαρμακείου',
            icon: 'pi pi-users',
            routerLink: '/user/search',
            roles: ['Pharmacist', 'ResponsiblePharmacist'],
            permissions: ['USER_MANAGEMENT'],
          }),
          this.menuItem({
            label: 'Επικυρωμένες Συνταγές (Όλα)',
            icon: 'pi pi-check-circle',
            routerLink: '/admin/verifications',
            permissions: ['ADMIN_VIEW'],
          }),
          this.menuItem({
            label: 'Αρχείο Ελέγχου',
            icon: 'pi pi-history',
            routerLink: '/admin/audit-logs',
            permissions: ['ADMIN_VIEW'],
          }),
          this.menuItem({
            label: 'Κατάσταση Συστήματος',
            icon: 'pi pi-heart',
            routerLink: '/admin/health',
            permissions: ['ADMIN_VIEW'],
          }),
          this.menuItem({
            label: 'Μετρήσεις Απόδοσης',
            icon: 'pi pi-chart-line',
            routerLink: '/admin/performance-metrics',
            permissions: ['ADMIN_VIEW'],
          }),
          this.menuItem({
            label: 'Στατιστικά Απόδοσης',
            icon: 'pi pi-chart-bar',
            routerLink: '/admin/performance-statistics',
            permissions: ['ADMIN_VIEW'],
          }),
          // this.menuItem({
          //   label: 'Ρόλοι',
          //   icon: 'pi pi-sitemap',
          //   routerLink: '/roles',
          //   command: () => this.itemClick.emit(),
          //   roles: ['Admin'],
          //   permissions: ['ROLE_MANAGEMENT'],
          // }),
          // this.menuItem({
          //   label: 'Διαχείριση Χρηστών',
          //   icon: 'pi pi-users',
          //   routerLink: '/user/search',
          //   permissions: ['users'],
          //   roles: ['Admin'],
          // }),
        ],
      },
      {
        label: 'Συστημα',
        expanded: true,
        items: [
          this.menuItem({
            label: 'Επικοινωνία',
            icon: 'pi pi-file',
            routerLink: '/contact',
            command: () => this.itemClick.emit(),
            permissions: ['CONTACT'],
          }),

          // this.menuItem({
          //   label: 'Οδηγίες Χρήσης',
          //   icon: 'pi pi-question-circle',
          //   routerLink: '',
          // }),
          this.menuItem({
            label: 'Ρυθμίσεις',
            icon: 'pi pi-cog',
            routerLink: '/settings',
            permissions: ['SETTINGS_UPDATE'],
          }),
        ],
      },
    ];
  }
}
